import { useEffect, useMemo, useState } from "react";
import { gql, useQuery } from "@apollo/client";
import { HelpCircle } from "lucide-react";
import { Button, EmptyState, Pagination } from "../../components/ui";
import {
  FaqListToolbar,
  type ToolbarFilters,
  type StatusTab,
} from "./faq-list/FaqListToolbar";
import { FaqRow, type FaqRowData } from "./faq-list/FaqRow";

const PER_PAGE = 15;

const ALL_FAQS = gql`
  query AllFaqsForListing($page: Int!, $perPage: Int!, $filter: FaqFilter) {
    allFaqs(
      page: $page
      perPage: $perPage
      sortField: "sortOrder"
      sortOrder: "ASC"
      filter: $filter
    ) {
      id
      question
      answer
      sortOrder
      showOnFaqPage
      updatedAt
    }
    _allFaqsMeta(filter: $filter) {
      count
    }
  }
`;

interface QueryData {
  allFaqs: FaqRowData[];
  _allFaqsMeta: { count: number };
}

const INITIAL_FILTERS: ToolbarFilters = {
  search: "",
  status: "all",
};

export function FaqsListing() {
  const [filters, setFilters] = useState<ToolbarFilters>(INITIAL_FILTERS);
  const [page, setPage] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [filters.search, filters.status]);

  const filterVar = useMemo(
    () => buildFilter(filters),
    [filters.search, filters.status],
  );

  const { data, previousData, loading, error, refetch } = useQuery<QueryData>(
    ALL_FAQS,
    {
      variables: { page, perPage: PER_PAGE, filter: filterVar },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
    },
  );

  const stable = data ?? previousData;
  const faqs = stable?.allFaqs ?? [];
  const total = stable?._allFaqsMeta?.count ?? 0;
  const initialLoad = loading && !stable;

  const filtersActive =
    filters.search.trim().length > 0 || filters.status !== "all";

  if (error && !stable) {
    return (
      <div className="grid grid-cols-12 gap-3">
        <section className="shadow-card col-span-12 rounded-lg bg-paper-hi p-8 text-center">
          <div className="mb-1 text-[14px] font-medium">
            Couldn't load FAQs
          </div>
          <div className="mb-4 text-[12.5px] text-ink-4">{error.message}</div>
          <Button variant="primary" onClick={() => refetch()}>
            Try again
          </Button>
        </section>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-3">
      <section className="shadow-card col-span-12 overflow-hidden rounded-lg bg-paper-hi">
        <div className="hair-b border-b flex items-center justify-between gap-3 px-5 py-4">
          <div>
            <h2 className="text-[15px] font-semibold">FAQs</h2>
            <p className="mt-0.5 text-[12px] text-ink-4">
              {filtersActive
                ? `${total} matching ${total === 1 ? "FAQ" : "FAQs"}`
                : `${total} total ${total === 1 ? "FAQ" : "FAQs"}`}
            </p>
          </div>
          <FaqListToolbar filters={filters} onFiltersChange={setFilters} />
        </div>

        {initialLoad ? (
          <RowSkeletons />
        ) : faqs.length === 0 ? (
          filtersActive ? (
            <EmptyState
              icon={<HelpCircle size={28} strokeWidth={1.5} />}
              heading="No matching FAQs"
              description="Try a different search term or clear the filter."
            />
          ) : (
            <EmptyState
              icon={<HelpCircle size={28} strokeWidth={1.5} />}
              heading="No FAQs yet"
              description="Write your first FAQ to start building out your help content."
            />
          )
        ) : (
          <div
            className={`divide-hair ${loading ? "opacity-60" : ""} transition-opacity`}
          >
            {faqs.map((f) => (
              <FaqRow key={f.id} faq={f} />
            ))}
          </div>
        )}

        {!initialLoad && total > 0 && (
          <div className="hair-t border-t bg-canvas/40 px-5 py-3">
            <Pagination
              page={page}
              perPage={PER_PAGE}
              total={total}
              onPageChange={setPage}
              unit="FAQ"
            />
          </div>
        )}
      </section>
    </div>
  );
}

function buildFilter(filters: ToolbarFilters):
  | {
      status?: string;
      q?: string;
    }
  | undefined {
  const search = filters.search.trim();
  const tabStatus = mapTabToStatus(filters.status);
  if (!search && !tabStatus) return undefined;
  return {
    ...(tabStatus ? { status: tabStatus } : {}),
    ...(search ? { q: search } : {}),
  };
}

function mapTabToStatus(tab: StatusTab): string | null {
  switch (tab) {
    case "on_faq_page":
      return "on_faq_page";
    case "hidden":
      return "hidden";
    default:
      return null;
  }
}

function RowSkeletons() {
  return (
    <div className="divide-hair">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="min-w-0 flex-1 space-y-2">
            <div className="pulse-soft h-3 w-1/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-4 w-3/4 rounded bg-canvas-2" />
            <div className="pulse-soft h-3 w-2/3 rounded bg-canvas-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
